import { NgClass, NgTemplateOutlet, TitleCasePipe } from '@angular/common'
import { ViewChild, OnInit, OnDestroy, Output, EventEmitter, TemplateRef, inject, Component, Input } from '@angular/core'
import { delay, of, take } from 'rxjs'

import {
  slideInRightOnEnterAnimation,
  slideOutRightOnLeaveAnimation,
} from 'angular-animations'

import { ResponsiveComponent, SettingsComponent } from '@nx-superprep/shared'
import { LongtailService, PrivateUserInfo, UserInfo, UserService } from '@nx-superprep/backend/firebase'

import { AuthComponent } from '../auth'
import { AvatarComponent } from "../avatar/avatar.component"
import { ProfileEditComponent } from "./edit/edit.component"
import { ProfileHelpComponent } from "./help/help.component"
import { ProfileReportComponent } from "./report/report.component"
import { ProfileSuggestComponent } from "./suggest/suggest.component"
import { NavigationBarComponent, NavigationBarState } from "../navigationbar/navigationbar.component"

import { LegalComponent, LegalType } from "../legal/legal.component";
import { JoinClassroomComponent } from '../join-classroom/join-classroom.component'
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal'

@Component({
  selector: 'pop-ui-profile',
  standalone: true,
  templateUrl: './profile.component.html',
  styleUrls: ['./profile.component.scss'],
  animations: [
    slideInRightOnEnterAnimation({ anchor: 'slideInRight', duration: 900, delay: 100 }),
    slideOutRightOnLeaveAnimation({ anchor: 'slideOutRight', duration: 900, delay: 100 }),
  ],
  queries: {
    "confirmLogout": new ViewChild("_confirmLogout"),
  },
  imports: [
    NgClass, NgTemplateOutlet, TitleCasePipe,
    ProfileEditComponent,
    ProfileHelpComponent,
    ProfileReportComponent,
    JoinClassroomComponent,
    SettingsComponent,
    ProfileSuggestComponent,
    NavigationBarComponent,
    AvatarComponent,
    LegalComponent
  ],
  providers: [BsModalService]
})
export class ProfileComponent extends ResponsiveComponent implements OnInit, OnDestroy {
  @Output() action = new EventEmitter<boolean>()

  public confirmLogout!: TemplateRef<unknown>
  private _bsModalRef!: BsModalRef

  private _entries: { name: string, title: string, active:boolean, hidden?:boolean }[] = []
  private _isOpenSlide = false

  private _mainNavigationState: NavigationBarState = {
    current: { route: 'profile', type: 'close-end' },
    title: '',
  }

  @Input() userInfo!: UserInfo & PrivateUserInfo | null

  get email() {return this.userService.activeUser?.email??'' }
  get profileName() { return this.userInfo?.profileName }

  content: string | undefined

  get mainNavigationState() {
    return this._mainNavigationState
  }
  set mainNavigationState(value) {
    this._mainNavigationState = value
    if (this._mainNavigationState.action) { this.onClose() }
  }
  private _subNavigationState: NavigationBarState = {
    current: { route: 'profile', type: 'close-end' },
    title: '',
  }
  get subNavigationState() {
    return this._subNavigationState
  }
  set subNavigationState(value) {
    this._subNavigationState = value
    if (this._subNavigationState.action) { this.onClose('sub') }
  }

  get isOpenSlide() {
    return this._isOpenSlide
  }
  set isOpenSlide(value: boolean) {
    this._isOpenSlide = value
  }

  onClose(name?: string) {
    if (name) {
      this.isOpenSlide = false
    } else {
      this.action.emit(false)
    }
  }

  cancel() {
    this._bsModalRef?.hide()
  }
  create() {
    this._bsModalRef?.hide()
    this._bsModalRef = this.modalService.show(AuthComponent, {animated: true, class: 'modal-md', initialState: {options: {state:'register'}}})
  }
  signout() {
    this._bsModalRef?.hide()
    this.userService.signOut().finally(() => this.router.navigate([''], { skipLocationChange: true }))
  }
  confirm() {
    this._bsModalRef?.hide()
    this._bsModalRef = this.modalService.show(this.confirmLogout, {animated:true, class:'modal-md signout-modal'})
  }

  get entries() {
    return this._entries
  }

  onAction(name: string) {
    this.longtailService.logPageView(name, `${window.location.href}/profile`, `/profile/${name}`)
    this.entries.forEach((e) => { e.active = e.name === name })
    switch(name) {
      case 'logout':
        if (this.userService.isGuest) {
          this.confirm()
        } else {
          this.signout()
        } break
      case 'edit':
        if (this.userService.isGuest) {
          this.create()
        } else {
          this.content = name
        } break
      default:
        this.content = name as LegalType
    }
    this.isOpenSlide = !!this.content
  }

  override ngOnInit() {
    super.ngOnInit()
    this._entries = [
      { name: 'join', title: 'Classroom Join Code', active:false, hidden: this.isWeb || !!this.userService.classroomId },
      { name: 'settings', title: 'settings', active:false, },
      { name: 'help', title: 'help', active:false, hidden: true },
      { name: 'report', title: 'report a bug', active:false, hidden: true },
      { name: 'suggestion', title: 'have a suggestion', active:false, hidden: true },
      //{ name: 'cookie', title: 'Manage cookies', active:false, hidden: this.userService.appSetting.debugMode !== true },
      { name: 'privacy', title: 'Privacy Policy', active:false },
      { name: 'tacos', title: 'Terms of Use', active:false },
      { name: 'logout', title: 'log out', active:false },
    ].filter((e) => !e.hidden);
    if (this.isWeb) {
      of(true).pipe(delay(100),take(1)).subscribe(() => {
        this.onAction('settings')
      })
    }
  }

 readonly longtailService = inject(LongtailService)
 readonly userService = inject(UserService)
 readonly modalService = inject(BsModalService)

  constructor() {
    super()
    super.rootClassName = 'profile-root-class-name'
  }
}
